import React, { useCallback } from 'react';
import { toArrayLiteral } from '@/utilities/toArrayLiteral';
import { EXCEPTION_TYPES } from '@onward-delivery/core';

export const useExceptionCallbacks = (
    { order, exceptionParams, route_id },
    { insertExceptionEvent, insertException, updateException }
) => {
    const createException = useCallback(
        (exception) => {
            const { __typename, items, images, mappings, ...rest } = exception;

            if (exceptionParams.action) {
                const attemptMappingIdsFromParams = Array.isArray(exceptionParams.attempt_mapping_ids)
                    ? exceptionParams.attempt_mapping_ids.filter(Boolean)
                    : [];
                const fallbackAttemptMappingIds = (order?.routes || [])
                    .filter(
                        (mapping) =>
                            mapping?.mapping_id &&
                            mapping?.route_id === route_id &&
                            (mapping?.duplicate_override == null || mapping?.duplicate_override === 'DISCARD')
                    )
                    .map((mapping) => mapping.mapping_id);
                const attemptMappingIds = exceptionParams.action.includes('ATTEMPT')
                    ? attemptMappingIdsFromParams.length > 0
                        ? attemptMappingIdsFromParams
                        : fallbackAttemptMappingIds
                    : [];

                const orderUpdates = exceptionParams.orderUpdates || {
                    ltl_dropoff_arrival: null,
                    completion_time: null,
                    loading_completed: null,
                    ltl_pickup_completed: null,
                    del_window_start: null,
                    del_window_end: null,
                    delivery_time_confirmed: null,
                    confirmation_source: null,
                    order_status: 'claimed',
                };
                // if ([EXCEPTION_TYPES.SHORTAGE, EXCEPTION_TYPES.DAMAGED, 'itemsTooDamaged'].includes(rest.type)) {
                //     orderUpdates.order_status = 'on_hold';
                // }
                const selectedItemIds = items
                    ? Array.from(
                          new Set(
                              Object.values(items)
                                  .map((item) => item?.item_id)
                                  .filter((item_id) => !!item_id)
                          )
                      )
                    : [];

                return insertExceptionEvent({
                    variables: {
                        exception_event: {
                            ev_action: exceptionParams.action,
                            ev_status: 'NOT_DELIVERED',
                            ex_call_or_text: rest.call_or_text,
                            ex_images: toArrayLiteral(images || []),
                            ex_instruction: rest.instruction,
                            ex_notes: rest.notes,
                            ex_reason: rest.reason,
                            ex_reported_at: rest.reported_at,
                            ex_resolution: rest.resolution,
                            ex_ring_or_knock: rest.ring_or_knock,
                            ex_route_id: route_id,
                            ex_type: rest.type,
                            ex_wait_time: rest.wait_time,
                            mapping_exception_type: items ? 'ITEM' : 'ORDER',
                            mapping_items: toArrayLiteral(selectedItemIds),
                            mapping_order: order.order_id,
                        },
                        order: {
                            order_id: order.order_id,
                            ...orderUpdates,
                        },
                        order_update_cols: Object.keys(orderUpdates),
                        attempt_mapping_ids: attemptMappingIds,
                    },
                });
            } else if (rest.exception_id) {
                const { order_id: existingOrderId, event_id } = mappings?.[0] || {};

                const selectedItemIds = new Set(
                    Object.values(items || {})
                        .map((item) => item?.item_id)
                        .filter((id) => !!id)
                );

                const existingItemIds = new Set((mappings || []).map((m) => m.item_id).filter((id) => !!id));
                const hasOrderMapping = (mappings || []).some((m) => !m.item_id);

                let mappingsToCreate = [];
                let mappingsToDelete = [];

                if (selectedItemIds.size > 0) {
                    const toCreateIds = Array.from(selectedItemIds).filter((id) => !existingItemIds.has(id));
                    mappingsToCreate = toCreateIds.map((item_id) => ({
                        item_id,
                        exception_id: rest.exception_id,
                        exception_type: 'ITEM',
                        order_id: existingOrderId || order.order_id,
                        event_id: event_id,
                    }));

                    mappingsToDelete = (mappings || [])
                        .filter((m) => !m.item_id || !selectedItemIds.has(m.item_id))
                        .map((m) => m.mapping_id);
                } else {
                    if (!hasOrderMapping) {
                        mappingsToCreate = [
                            {
                                exception_id: rest.exception_id,
                                exception_type: 'ORDER',
                                order_id: existingOrderId || order.order_id,
                                event_id: event_id,
                            },
                        ];
                    }
                    mappingsToDelete = (mappings || []).filter((m) => !!m.item_id).map((m) => m.mapping_id);
                }

                return updateException({
                    variables: {
                        exception_id: rest.exception_id,
                        exception: {
                            ...rest,
                            images: toArrayLiteral(images || []),
                        },
                        mappingsToCreate,
                        mappingsToDelete,
                    },
                });
            } else {
                const itemValues = items ? Object.values(items) : [];
                const uniqueItemIds = Array.from(new Set(itemValues.map((i) => i?.item_id).filter((id) => !!id)));
                return insertException({
                    variables: {
                        exception: {
                            ...rest,
                            images: toArrayLiteral(images || []),
                            mappings: {
                                data:
                                    items && uniqueItemIds.length > 0
                                        ? uniqueItemIds.map((item_id) => ({
                                              exception_type: 'ITEM',
                                              order_id: order.order_id,
                                              item_id,
                                          }))
                                        : [
                                              {
                                                  exception_type: 'ORDER',
                                                  order_id: order.order_id,
                                              },
                                          ],
                            },
                        },
                    },
                });
            }
        },
        [order, exceptionParams]
    );

    return { createException };
};
